// React bindings for the send outbox (lib/send-outbox.ts).

import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { setOutboxNavigator } from './outbox-jobs';
import { requestRestore } from './outbox-restore';
import { startOutboxToasts, type OutboxToastApi } from './outbox-toasts';
import { outbox, type OutboxJobView } from './send-outbox';

/** The jobs for one lane (a ticket id, or 'compose'), oldest first. Re-renders
 *  on any outbox change. */
export function useOutboxJobs(key: string | undefined): OutboxJobView[] {
  const all = useSyncExternalStore(outbox.subscribe, outbox.getSnapshot, outbox.getSnapshot);
  return useMemo(() => (key ? all.filter((j) => j.key === key) : []), [all, key]);
}

/** A clock for countdown labels. Ticks only while `active`, so an idle page
 *  isn't re-rendering four times a second. */
export function useNow(active: boolean, intervalMs = 250): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const t = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(t);
  }, [active, intervalMs]);
  return now;
}

/** Mount once (App, inside the router). Shows the "Sending in 5s… Undo" toast
 *  for every queued message on whatever page the person is on, and gives the
 *  outbox's toasts a way to open a ticket without reloading the app. */
export function useSendOutboxToasts(): void {
  const navigate = useNavigate();

  useEffect(() => {
    setOutboxNavigator((to) => navigate(to));
    return () => setOutboxNavigator(null);
  }, [navigate]);

  useEffect(
    () =>
      startOutboxToasts(outbox, toast as unknown as OutboxToastApi, {
        onEdit: (job) => void requestRestore(job),
      }),
    [],
  );
}
